import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const getMembershipBase = () => {
  return `${config.BASE_URL}/membership-control`;
};


async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

/** User-facing copy: hide raw 5xx / DB details from the UI. */
function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

export const getMemberships = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.name) params.append("name", filters.name);
  if (filters.name_exact) params.append("name_exact", filters.name_exact);
  if (filters.price_match != null && filters.price_match !== "")
    params.append("price_match", String(filters.price_match));
  if (
    filters.registration_fee_match != null &&
    filters.registration_fee_match !== ""
  )
    params.append(
      "registration_fee_match",
      String(filters.registration_fee_match)
    );
  if (filters.discount_match != null && filters.discount_match !== "")
    params.append("discount_match", String(filters.discount_match));
  if (
    filters.max_students_allowed_match != null &&
    filters.max_students_allowed_match !== ""
  )
    params.append(
      "max_students_allowed_match",
      String(filters.max_students_allowed_match)
    );
  if (
    filters.total_students_registered_match != null &&
    filters.total_students_registered_match !== ""
  )
    params.append(
      "total_students_registered_match",
      String(filters.total_students_registered_match)
    );
  if (
    filters.total_amount_paid_match != null &&
    filters.total_amount_paid_match !== ""
  )
    params.append(
      "total_amount_paid_match",
      String(filters.total_amount_paid_match)
    );
  if (filters.description_contains)
    params.append("description_contains", String(filters.description_contains));
  if (filters.benefits_contains)
    params.append("benefits_contains", String(filters.benefits_contains));
  if (filters.type) params.append("type", filters.type);
  if (filters.status) params.append("status", filters.status);
  if (filters.duration) params.append("duration", filters.duration);
  if (filters.membership_type_id != null && filters.membership_type_id !== "")
    params.append("membership_type_id", String(filters.membership_type_id));
  if (filters.duration_type_id != null && filters.duration_type_id !== "")
    params.append("duration_type_id", String(filters.duration_type_id));
  if (filters.fee_type_id != null && filters.fee_type_id !== "")
    params.append("fee_type_id", String(filters.fee_type_id));
  if (filters.is_active != null && filters.is_active !== "")
    params.append("is_active", String(filters.is_active));
  if (filters.page) params.append("page", String(filters.page));
  if (filters.limit) params.append("limit", String(filters.limit));

  const q = params.toString();
  const res = await authFetch(`${getMembershipBase()}${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Failed to fetch memberships"));
  }
  return data;
};


export const getMembershipDashboard = async () => {
  const res = await authFetch(`${getMembershipBase()}/dashboard`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch membership dashboard"),
    );
  }
  return data;
};

export const getMembershipLookups = async () => {
  const res = await authFetch(`${getMembershipBase()}/lookups`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch membership lookups"),
    );
  }
  return data;
};

export const getMembershipById = async (id) => {
  const res = await authFetch(
    `${getMembershipBase()}/${encodeURIComponent(String(id))}`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch membership details"),
    );
  }
  return data;
};

export const createMembership = async (data, csrfToken) => {
  try {
    const res = await authFetch(getMembershipBase(), {
      method: "POST",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(data),
    });
    const payload = await parseJson(res);
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: messageForResponse(
            res,
            payload,
            "Failed to create membership",
          ),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : "Failed to create membership",
      },
    };
  }
};

export const updateMembership = async (id, data, csrfToken) => {
  const res = await authFetch(
    `${getMembershipBase()}/${encodeURIComponent(String(id))}`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify(data),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to update membership"),
    );
  }
  return payload;
};

export const updateStatus = async (id, statusId, csrfToken) => {
  const res = await authFetch(
    `${getMembershipBase()}/${encodeURIComponent(String(id))}/status`,
    {
      method: "PATCH",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
      body: JSON.stringify({ statusId }),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to update membership status"),
    );
  }
  return payload;
};

export const deleteMembership = async (id, csrfToken) => {
  const res = await authFetch(
    `${getMembershipBase()}/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(
        csrfToken ? { "X-CSRF-Token": csrfToken } : {},
      ),
    },
  );
  const payload = await parseJson(res);
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete membership"),
    );
  }
  return payload;
};
